// ServiceMonitorPage — Deye hübriidsüsteemi monitooring (hoolduse 1–48 h mõõtmine + aruanne).
// Backend: /api/parks/:slot/deye-monitor* (services/DeyeMonitorService.js). AINULT lugemine —
// inverterisse ei kirjutata; juhtimise teeb Deye pilv / juhtimisteenus, aruanne annab soovitused.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import { GoodweMonitorPanel } from './GoodweMonitorPanel';
import {
  ComposedChart, BarChart, Bar, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';

const DURATIONS = [1, 6, 24, 48];
const STATUS = {
  running: ['Käib', '#2563eb'], finalizing: ['Arvutan aruannet', '#7c3aed'], done: ['Valmis', '#16a34a'],
  stopped: ['Lõpetatud varem', '#d97706'], failed: ['Ebaõnnestus', '#dc2626'],
};
const SEV = { critical: ['Kriitiline', '#dc2626'], warning: ['Hoiatus', '#d97706'], info: ['Info', '#2563eb'] };
const C = { solar: '#f59e0b', solar2: '#ea580c', load: '#3f3f46', grid: '#2563eb', battery: '#16a34a', temp: '#e11d48', cold: '#0ea5e9' };

const fmt = (v, d = 1) => (v == null || !Number.isFinite(Number(v)) ? '—'
  : Number(v).toLocaleString('et-EE', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtDT = (v) => (v ? new Date(v).toLocaleString('et-EE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtT = (ms) => new Date(ms).toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '14px 16px', marginBottom: 16 };
const label = { fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
const chip = (active) => ({
  padding: '4px 10px', borderRadius: 3, border: `1px solid ${active ? '#18181b' : 'var(--border)'}`,
  background: active ? '#18181b' : 'white', color: active ? 'white' : 'var(--text-2)',
  fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer',
});
const btn = (primary, color) => ({
  padding: '6px 12px', borderRadius: 4, border: `1px solid ${primary ? (color || '#166534') : 'var(--border-strong)'}`,
  background: primary ? (color || '#166534') : 'white', color: primary ? 'white' : 'var(--text-2)',
  fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
});
const numInput = { width: 80, padding: '4px 6px', borderRadius: 3, border: '1px solid var(--border-strong)', fontSize: '0.72rem', fontFamily: 'JetBrains Mono, monospace' };

export function ServiceMonitorPage({ wsConnected }) {
  const { data: parksData } = useParks();
  const deyeParks = useMemo(() => (parksData?.parks || [])
    .filter((p) => p.name && p.meter_protocol === 'deye_v5')
    .map((p) => ({ slot: p.slot, name: p.name, online: p.online })), [parksData]);

  const [slot, setSlot] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [hours, setHours] = useState(24);
  const [warranty, setWarranty] = useState({ cycles: 6000, dod_pct: 90, years: 10, ref_temp_c: 25 });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  // Vaheleht: Deye hübriid (aku + PV + seaded) või Goodwe parkide võrdlus (stringid)
  const [mode, setMode] = useState(null);
  const [updated, setUpdated] = useState(null);

  useEffect(() => {
    if (!deyeParks.length) return;
    if (!deyeParks.some((p) => p.slot === slot)) setSlot((deyeParks.find((p) => p.online) || deyeParks[0]).slot);
  }, [deyeParks, slot]);

  const running = sessions.find((s) => s.status === 'running' || s.status === 'finalizing');

  const loadSessions = useCallback(async () => {
    if (slot == null) return;
    try {
      const r = await fetch(`/api/parks/${slot}/deye-monitor`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setSessions(j.sessions || []);
      setUpdated(Date.now());
    } catch (e) { setError(e.message); }
  }, [slot]);

  useEffect(() => { setSessions([]); setSelectedId(null); setDetail(null); loadSessions(); }, [slot, loadSessions]);
  // Töötava sessiooni ajal värskenda nimekirja (edenemine, valmimine)
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(loadSessions, 30_000);
    return () => clearInterval(t);
  }, [running, loadSessions]);
  // Vaikimisi ava uusim valmis aruanne
  useEffect(() => {
    if (selectedId == null) {
      const last = sessions.find((s) => s.status === 'done' || s.status === 'stopped');
      if (last) setSelectedId(last.id);
    }
  }, [sessions, selectedId]);

  const loadDetail = useCallback(async (id) => {
    if (id == null || slot == null) return;
    setLoadingDetail(true);
    try {
      const r = await fetch(`/api/parks/${slot}/deye-monitor/${id}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setDetail(j);
    } catch (e) { setError(e.message); setDetail(null); } finally { setLoadingDetail(false); }
  }, [slot]);
  useEffect(() => { if (selectedId != null) loadDetail(selectedId); }, [selectedId, loadDetail]);
  // Valitud sessioon sai valmis → lae lõplik aruanne
  const selStatus = sessions.find((s) => s.id === selectedId)?.status;
  useEffect(() => { if (selStatus === 'done' || selStatus === 'stopped') loadDetail(selectedId); }, [selStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/parks/${slot}/deye-monitor`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours, warranty }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      await loadSessions();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const stop = async (id) => {
    if (!window.confirm('Lõpeta mõõtmine nüüd? Aruanne koostatakse seni kogutud andmetest.')) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/parks/${slot}/deye-monitor/${id}/stop`, { method: 'POST' });
      if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
      await loadSessions();
      setSelectedId(id);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Kustuta see mõõtmine ja aruanne?')) return;
    const r = await fetch(`/api/parks/${slot}/deye-monitor/${id}`, { method: 'DELETE' });
    if (!r.ok) { setError((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`); return; }
    if (selectedId === id) { setSelectedId(null); setDetail(null); }
    loadSessions();
  };
  const openHtml = (id) => window.open(`/api/parks/${slot}/deye-monitor/${id}/report.html`, '_blank', 'noopener');
  const downloadJson = (id) => { window.location.href = `/api/parks/${slot}/deye-monitor/${id}/export`; };

  const activeMode = mode || (deyeParks.length ? 'deye' : 'goodwe');
  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Monitooring" connected={wsConnected} lastUpdate={updated} />
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
          {[['deye', 'Deye hübriid'], ['goodwe', 'Goodwe võrdlus (stringid)']].map(([k, t]) => (
            <button key={k} onClick={() => setMode(k)} style={{ ...chip(activeMode === k), padding: '6px 14px', fontSize: '0.75rem' }}>{t}</button>
          ))}
        </div>
        {activeMode === 'goodwe' && <GoodweMonitorPanel />}
        {activeMode === 'deye' && (<>
        {deyeParks.length === 0 && (
          <div style={{ ...card, color: 'var(--text-3)', fontSize: '0.8rem' }}>
            Monitooring on saadaval Deye hübriidparkidele (pargi tüüp „Deye hübriid" seadistustes).
          </div>
        )}
        {deyeParks.length > 0 && (
          <div style={card}>
            <div style={label}>Park</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {deyeParks.map((p) => (
                <button key={p.slot} onClick={() => setSlot(p.slot)} style={{ ...chip(p.slot === slot), opacity: p.online ? 1 : 0.6 }}>
                  #{p.slot} {p.name}{!p.online && ' ·off'}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div style={{ ...card, borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b', fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between' }}>
            <span>{error}</span><button onClick={() => setError(null)} style={{ ...btn(false), padding: '2px 8px' }}>×</button>
          </div>
        )}

        {slot != null && (
          <div style={card}>
            {running ? <RunningSession s={running} onStop={() => stop(running.id)} onPartial={() => { setSelectedId(running.id); loadDetail(running.id); }}
                                       onHtml={() => openHtml(running.id)} busy={busy} />
              : (
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <div style={label}>Uus mõõtmine · kestus</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {DURATIONS.map((h) => <button key={h} onClick={() => setHours(h)} style={chip(hours === h)}>{h} h</button>)}
                    </div>
                  </div>
                  <div>
                    <div style={label}>Aku garantii (andmelehelt)</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.7rem', color: 'var(--text-2)' }}>
                      {[['cycles', 'tsüklit'], ['dod_pct', '% DOD'], ['years', 'aastat'], ['ref_temp_c', '°C juures']].map(([k, u]) => (
                        <span key={k} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <input type="number" value={warranty[k]} style={numInput}
                            onChange={(e) => setWarranty((w) => ({ ...w, [k]: e.target.value === '' ? '' : Number(e.target.value) }))} />{u}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button onClick={start} disabled={busy} style={{ ...btn(true), opacity: busy ? 0.6 : 1 }}>▶ Alusta mõõtmist</button>
                  <div style={{ flex: '1 1 100%', fontSize: '0.66rem', color: 'var(--text-3)' }}>
                    Mõõtmise ajal loetakse inverterit ja BMS-i 10 s tagant; alguses ja lõpus loetakse seaded. Ainult lugemine — inverteri seadeid ei muudeta.
                    PV stringide andmed ja asukoht (oodatud pinge/toodang) sisesta Hooldus → Seadistused.
                  </div>
                </div>
              )}
          </div>
        )}

        {sessions.length > 0 && (
          <div style={card}>
            <div style={label}>Mõõtmised</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: '0.6rem', textTransform: 'uppercase' }}>
                  <th style={th}>Algus</th><th style={th}>Kestus</th><th style={th}>Olek</th><th style={th}>PV</th><th style={th}>Tarbimine</th>
                  <th style={th}>Aku SoH</th><th style={th}>Leiud</th><th style={th}>Alustas</th><th style={th} />
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const [st, col] = STATUS[s.status] || [s.status, '#71717a'];
                  const sum = s.summary || {};
                  const hoursPlanned = Math.round((s.ends_at - s.started_at) / 3600_000);
                  return (
                    <tr key={s.id} onClick={() => setSelectedId(s.id)}
                      style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', background: s.id === selectedId ? '#f4f4f5' : 'transparent' }}>
                      <td style={td}>{fmtDT(s.started_at)}</td>
                      <td style={td}>{hoursPlanned} h</td>
                      <td style={td}><span style={{ color: col, fontWeight: 600 }}>{st}</span></td>
                      <td style={td}>{sum.pv_kwh != null ? `${fmt(sum.pv_kwh)} kWh` : '—'}</td>
                      <td style={td}>{sum.load_kwh != null ? `${fmt(sum.load_kwh)} kWh` : '—'}</td>
                      <td style={td}>{sum.soh_pct != null ? `${fmt(sum.soh_pct, 0)} %` : '—'}</td>
                      <td style={td}>
                        {s.summary ? (<>
                          {sum.critical > 0 && <span style={{ color: SEV.critical[1], fontWeight: 600, marginRight: 6 }}>{sum.critical} kriitilist</span>}
                          {sum.warnings > 0 && <span style={{ color: SEV.warning[1], fontWeight: 600 }}>{sum.warnings} hoiatust</span>}
                          {!sum.critical && !sum.warnings && <span style={{ color: '#16a34a' }}>korras</span>}
                        </>) : (s.error ? <span style={{ color: '#dc2626' }}>{s.error}</span> : '—')}
                      </td>
                      <td style={{ ...td, color: 'var(--text-3)' }}>{s.created_by || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {(s.status === 'done' || s.status === 'stopped') && (<>
                          <button onClick={() => openHtml(s.id)} style={{ ...btn(false), padding: '3px 8px', marginRight: 4 }}>Aruanne</button>
                          <button onClick={() => downloadJson(s.id)} style={{ ...btn(false), padding: '3px 8px', marginRight: 4 }}>JSON</button>
                        </>)}
                        {s.status !== 'running' && s.status !== 'finalizing' && (
                          <button onClick={() => remove(s.id)} title="Kustuta" style={{ ...btn(false), padding: '3px 8px', color: '#dc2626' }}>×</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {loadingDetail && <div style={{ ...card, fontSize: '0.75rem', color: 'var(--text-3)' }}>Laen aruannet…</div>}
        {!loadingDetail && detail?.report && <ReportView detail={detail} onHtml={() => openHtml(detail.id)} onJson={() => downloadJson(detail.id)} />}
        {!loadingDetail && detail && !detail.report && (
          <div style={{ ...card, fontSize: '0.75rem', color: 'var(--text-3)' }}>Aruannet pole — {detail.error || 'andmed puuduvad'}.</div>
        )}
        </>)}
      </div>
    </div>
  );
}

const th = { padding: '4px 6px', fontWeight: 600 };
const td = { padding: '6px 6px', verticalAlign: 'top' };

function RunningSession({ s, onStop, onPartial, onHtml, busy }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 10_000); return () => clearInterval(t); }, []);
  const pct = Math.max(0, Math.min(100, (100 * (now - s.started_at)) / (s.ends_at - s.started_at)));
  const left = Math.max(0, s.ends_at - now);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={label}>Mõõtmine käib</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {fmtDT(s.started_at)} → {fmtDT(s.ends_at)}
            <span style={{ fontWeight: 400, color: 'var(--text-3)', marginLeft: 8 }}>
              {s.status === 'finalizing' ? 'arvutan aruannet…' : `jäänud ${Math.floor(left / 3600_000)} h ${Math.round((left % 3600_000) / 60_000)} min`}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onPartial} style={btn(false)}>Vahearuanne</button>
          <button onClick={onHtml} style={btn(false)}>Prinditav vahearuanne</button>
          <button onClick={onStop} disabled={busy || s.status !== 'running'} style={btn(true, '#dc2626')}>■ Lõpeta</button>
        </div>
      </div>
      <div style={{ height: 6, background: '#e4e4e7', borderRadius: 3, marginTop: 10, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#16a34a', transition: 'width .5s' }} />
      </div>
      <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', marginTop: 4 }}>
        {fmt(pct, 0)} % · pollimine 10 s · {s.created_by || '—'}
      </div>
    </div>
  );
}

function Kpi({ title, value, sub, tone }) {
  const bg = tone === 'good' ? '#ecfdf3' : tone === 'warn' ? '#fff7ed' : '#f4f4f5';
  return (
    <div style={{ background: bg, borderRadius: 6, padding: '9px 11px' }}>
      <div style={{ fontSize: '0.58rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.62rem', color: 'var(--text-3)' }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, height = 230, note }) {
  return (
    <div style={card}>
      <div style={label}>{title}</div>
      <div style={{ height }}><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div>
      {note && <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', marginTop: 4 }}>{note}</div>}
    </div>
  );
}

const axisTick = { fontSize: 10, fill: '#71717a' };
const xAxis = (data) => (
  <XAxis dataKey="t" type="number" scale="time" domain={[data[0]?.t ?? 'dataMin', data[data.length - 1]?.t ?? 'dataMax']}
    tickFormatter={fmtT} tick={axisTick} minTickGap={40} />
);
const tooltip = <Tooltip labelFormatter={(v) => fmtDT(v)} formatter={(v, n) => [typeof v === 'number' ? fmt(v, 2) : v, n]} contentStyle={{ fontSize: 11 }} />;

function ReportView({ detail, onHtml, onJson }) {
  const devices = detail.report.devices || [];
  const [devIdx, setDevIdx] = useState(0);
  const dev = devices[Math.min(devIdx, devices.length - 1)];
  const rep = dev?.report;
  const data = useMemo(() => (rep?.timeseries_5min || []).map((b) => ({ ...b, t: Date.parse(b.ts), buy_s: b.buy_price != null ? b.buy_price * 100 : null })), [rep]);
  if (!rep) return null;
  const b = rep.battery, cap = b.capacity_estimate, temp = b.temperature, cons = rep.consumption || {}, eco = rep.economics;
  const recs = [...(rep.recommendations || [])].sort((x, y) => ['critical', 'warning', 'info'].indexOf(x.severity) - ['critical', 'warning', 'info'].indexOf(y.severity));
  const bands = (types) => (rep.problems || []).filter((p) => types.includes(p.type)).map((p) => ({ x1: Date.parse(p.from), x2: Date.parse(p.to) + 60_000, label: p.label }));
  const th2 = rep.grid.thresholds;

  return (
    <div>
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={label}>{detail.report.partial ? 'Vahearuanne (mõõtmine käib)' : 'Aruanne'}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {fmtDT(rep.period.from)} – {fmtDT(rep.period.to)} · {fmt(rep.period.hours, 1)} h · {rep.coverage.points} mõõtmist ({fmt(rep.coverage.pct, 0)} %)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {devices.length > 1 && devices.map((d, i) => (
            <button key={d.key} onClick={() => setDevIdx(i)} style={chip(i === devIdx)}>{d.label || d.key}</button>
          ))}
          <button onClick={onHtml} style={btn(true)}>Prinditav aruanne (PDF)</button>
          <button onClick={onJson} style={btn(false)}>JSON (AI-analüüs)</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 16 }}>
        <Kpi title="PV toodang" value={`${fmt(rep.pv.energy_kwh)} kWh`} sub={`tipp ${fmt(rep.pv.peak_kw)} kW`} />
        <Kpi title="Tarbimine" value={`${fmt(cons.load_kwh)} kWh`} sub={`baaskoormus ${fmt(cons.base_load_kw, 2)} kW`} />
        <Kpi title="Omavarustatus" value={`${fmt(cons.self_sufficiency_pct, 0)} %`} sub="oma PV osa tarbimisest" />
        <Kpi title="Võrgust / võrku" value={`${fmt(rep.grid.import_kwh)} / ${fmt(rep.grid.export_kwh)}`} sub={eco ? `${fmt(eco.import_cost_eur, 2)} € / ${fmt(eco.export_revenue_eur, 2)} €` : 'kWh'} />
        <Kpi title="Aku SoH" value={cap.soh_pct != null ? `${fmt(cap.soh_pct, 0)} %` : '—'} sub={cap.capacity_kwh_median != null ? `${fmt(cap.capacity_kwh_median)} kWh / ${fmt(cap.capacity_ah_median, 0)} Ah` : 'vajab ≥10 % SoC lõiku'} tone={cap.soh_pct != null ? (cap.soh_pct < 90 ? 'warn' : 'good') : ''} />
        <Kpi title="Tsükleid" value={fmt(b.cycles.equivalent_full_cycles, 2)} sub={`max DOD ${fmt(b.cycles.max_dod_pct, 0)} %`} />
        <Kpi title="Aku temperatuur" value={`${fmt(temp.overall?.min, 0)}…${fmt(temp.overall?.max, 0)} °C`} sub={`laadimine <10 °C: ${fmt(temp.minutes_charging_below_10c, 0)} min`} tone={(temp.minutes_charging_below_10c ?? 0) > 5 ? 'warn' : ''} />
        {eco && <Kpi title="Aku laadimine / väärtus" value={`${fmt(eco.battery_charge_avg_price_eur_kwh * 100)} / ${fmt(eco.battery_discharge_avg_price_eur_kwh * 100)}`} sub="s/kWh" tone={eco.battery_spread_eur_kwh > 0 ? 'good' : 'warn'} />}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 16 }}>
        <div style={card}>
          <div style={label}>Tähelepanekud ja soovitused</div>
          {recs.length === 0 && <div style={{ fontSize: '0.75rem', color: '#16a34a' }}>Kõrvalekaldeid ei leitud.</div>}
          {recs.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '7px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
              <span style={{ flex: 'none', width: 64, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: SEV[r.severity]?.[1] }}>
                {SEV[r.severity]?.[0]}<div style={{ color: 'var(--text-3)', fontWeight: 500 }}>{r.area}</div>
              </span>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{r.title}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-2)' }}>{r.detail}</div>
                {r.action && <div style={{ fontSize: '0.64rem', color: 'var(--text-3)', marginTop: 2 }}>→ {r.action.task} · {r.action.who} · {r.action.where}</div>}
              </div>
            </div>
          ))}
        </div>
        <div style={card}>
          <div style={label}>Mis on korras</div>
          {(rep.positives || []).map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, padding: '4px 0', fontSize: '0.7rem' }}>
              <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
              <div><div style={{ fontWeight: 500 }}>{p.title}</div><div style={{ color: 'var(--text-3)', fontSize: '0.64rem' }}>{p.detail}</div></div>
            </div>
          ))}
        </div>
      </div>

      <ChartCard title="Võimsused (kW) — PV, tarbimine, võrk (+ost), aku (+laadimine)">
        <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          {xAxis(data)}
          <YAxis tick={axisTick} />
          {tooltip}
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine y={0} stroke="#a1a1aa" />
          <Area dataKey="pv_kw" name="PV" stroke={C.solar} fill={C.solar} fillOpacity={0.25} dot={false} isAnimationActive={false} />
          <Line dataKey="load_kw" name="Tarbimine" stroke={C.load} dot={false} strokeWidth={1.6} isAnimationActive={false} />
          <Line dataKey="grid_kw" name="Võrk" stroke={C.grid} dot={false} strokeWidth={1.2} isAnimationActive={false} />
          <Line dataKey="bat_kw" name="Aku" stroke={C.battery} dot={false} strokeWidth={1.2} isAnimationActive={false} />
        </ComposedChart>
      </ChartCard>

      <ChartCard title="Aku laetus (SoC %) ja temperatuur (°C) — sinine = laadimine alla 10 °C, punane = üle 40 °C, roheline riba = garantii viitevahemik"
        note={`Temperatuur ${fmt(temp.overall?.min)}…${fmt(temp.overall?.max)} °C, viitevahemikus (${temp.reference_band}) ${fmt(temp.pct_in_reference_band, 0)} % ajast.`}>
        <ComposedChart data={data} margin={{ top: 5, right: 0, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          {xAxis(data)}
          <YAxis yAxisId="soc" domain={[0, 100]} tick={axisTick} />
          <YAxis yAxisId="t" orientation="right" tick={axisTick} domain={[(min) => Math.min(0, Math.floor(min)), (max) => Math.ceil(Math.max(35, max))]} />
          {tooltip}
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceArea yAxisId="t" y1={20} y2={30} fill="#16a34a" fillOpacity={0.08} />
          {bands(['charging_cold']).map((x, i) => <ReferenceArea key={`c${i}`} yAxisId="soc" x1={x.x1} x2={x.x2} fill={C.cold} fillOpacity={0.22} />)}
          {bands(['battery_hot']).map((x, i) => <ReferenceArea key={`h${i}`} yAxisId="soc" x1={x.x1} x2={x.x2} fill="#dc2626" fillOpacity={0.18} />)}
          <Area yAxisId="soc" dataKey="soc" name="SoC %" stroke={C.battery} fill={C.battery} fillOpacity={0.15} dot={false} isAnimationActive={false} />
          <Line yAxisId="t" dataKey="bat_temp" name="Aku °C" stroke={C.temp} dot={false} strokeWidth={1.6} isAnimationActive={false} />
        </ComposedChart>
      </ChartCard>

      <ChartCard title="PV stringid — võimsus (kW) ja pinge (V) MPPT kaupa">
        <ComposedChart data={data} margin={{ top: 5, right: 0, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          {xAxis(data)}
          <YAxis yAxisId="p" tick={axisTick} />
          <YAxis yAxisId="v" orientation="right" tick={axisTick} />
          {tooltip}
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area yAxisId="p" dataKey="pv1_kw" name="MPPT1 kW" stroke={C.solar} fill={C.solar} fillOpacity={0.25} dot={false} isAnimationActive={false} />
          <Area yAxisId="p" dataKey="pv2_kw" name="MPPT2 kW" stroke={C.solar2} fill={C.solar2} fillOpacity={0.18} dot={false} isAnimationActive={false} />
          <Line yAxisId="v" dataKey="pv1_v" name="MPPT1 V" stroke={C.solar} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
          <Line yAxisId="v" dataKey="pv2_v" name="MPPT2 V" stroke={C.solar2} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ChartCard>
      {(rep.pv_strings || []).length > 0 && (
        <div style={card}>
          <div style={label}>MPPT stringid vs paneelide andmed</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: '0.6rem', textTransform: 'uppercase' }}>
              <th style={th}>MPPT</th><th style={th}>Paneelid</th><th style={th}>Toodang</th><th style={th}>Oodatud</th><th style={th}>PR</th>
              <th style={th}>Vmp mõõdetud/oodatud</th><th style={th}>Max V / Voc STC</th><th style={th}>Hinnang</th></tr></thead>
            <tbody>
              {rep.pv_strings.map((s) => {
                const bad = s.vmp_measured_vs_expected != null && (s.vmp_measured_vs_expected < 0.92 || s.vmp_measured_vs_expected > 1.06);
                return (
                  <tr key={s.mppt} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={td}>MPPT{s.mppt}{s.label ? ` · ${s.label}` : ''}</td>
                    <td style={td}>{s.panels} · {fmt(s.kwp, 2)} kWp</td>
                    <td style={td}>{fmt(s.energy_kwh, 2)} kWh</td>
                    <td style={td}>{s.expected_kwh != null ? `${fmt(s.expected_kwh, 2)} kWh` : '—'}</td>
                    <td style={td}>{fmt(s.performance_ratio, 2)}</td>
                    <td style={{ ...td, color: bad ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{s.vmp_measured_vs_expected != null ? `${fmt(s.vmp_measured_vs_expected * 100, 1)} %` : '—'}</td>
                    <td style={td}>{fmt(rep.pv[`mppt${s.mppt}`]?.voltage_max_v, 0)} / {fmt(s.stc.voc_v, 0)} V</td>
                    <td style={{ ...td, color: 'var(--text-2)' }}>{s.vmp_note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ChartCard title={`Võrgupinge (V, 5 min max) — ${fmt(th2.u_10min, 0)} V = 1,10 Un, ${fmt(th2.u_high, 1)} V = 1,15 Un`}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          {xAxis(data)}
          <YAxis tick={axisTick} domain={[215, 270]} />
          {tooltip}
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {bands(['grid_overvoltage']).map((x, i) => <ReferenceArea key={i} x1={x.x1} x2={x.x2} fill="#d97706" fillOpacity={0.18} />)}
          <ReferenceLine y={th2.u_10min} stroke="#d97706" strokeDasharray="5 3" />
          <ReferenceLine y={th2.u_high} stroke="#dc2626" strokeDasharray="5 3" />
          <Line dataKey="v_l1_max" name="L1" stroke="#0f766e" dot={false} isAnimationActive={false} />
          <Line dataKey="v_l2_max" name="L2" stroke="#7c3aed" dot={false} isAnimationActive={false} />
          <Line dataKey="v_l3_max" name="L3" stroke="#be185d" dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ChartCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
        <ChartCard title="Keskmine tunniprofiil (kW)" height={200}
          note={`Öine baaskoormus ${fmt(cons.base_load_kw, 2)} kW ≈ ${fmt(cons.base_load_kwh_per_day)} kWh ööpäevas.`}>
          <BarChart data={cons.hourly_profile || []} margin={{ top: 5, right: 0, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
            <XAxis dataKey="hour" tick={axisTick} />
            <YAxis tick={axisTick} />
            <Tooltip formatter={(v) => fmt(v, 2)} labelFormatter={(h) => `kell ${h}`} contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="load_kw" name="Tarbimine" fill={C.load} isAnimationActive={false} />
            <Bar dataKey="pv_kw" name="PV" fill={C.solar} isAnimationActive={false} />
          </BarChart>
        </ChartCard>
        {data.some((x) => x.buy_s != null) ? (
          <ChartCard title="Aku (kW) vs elektri ostuhind (s/kWh) — juhtimise kvaliteet" height={200}>
            <ComposedChart data={data} margin={{ top: 5, right: 0, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              {xAxis(data)}
              <YAxis yAxisId="k" tick={axisTick} />
              <YAxis yAxisId="p" orientation="right" tick={axisTick} />
              {tooltip}
              <ReferenceLine yAxisId="k" y={0} stroke="#a1a1aa" />
              <Area yAxisId="k" dataKey="bat_kw" name="Aku kW" stroke={C.battery} fill={C.battery} fillOpacity={0.3} dot={false} isAnimationActive={false} />
              <Line yAxisId="p" dataKey="buy_s" name="Ostuhind s/kWh" type="stepAfter" stroke="#18181b" dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ChartCard>
        ) : <div />}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
        <div style={card}>
          <div style={label}>Elektrilevi nõuded (seaded mõõtmise alguses)</div>
          {(rep.grid_code_checks || []).length === 0 && <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>Seadete hetktõmmis puudub.</div>}
          {(rep.grid_code_checks || []).map((g, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '18px 1fr auto', gap: 6, padding: '3px 0', fontSize: '0.68rem', borderTop: i ? '1px solid var(--border)' : 'none' }}>
              <span style={{ color: g.ok ? '#16a34a' : g.mandatory ? '#dc2626' : '#d97706', fontWeight: 700 }}>{g.ok ? '✓' : '✗'}</span>
              <span>{g.req}</span><span style={{ color: 'var(--text-3)' }}>{g.now}</span>
            </div>
          ))}
        </div>
        <div style={card}>
          <div style={label}>Aku mahutavuse mõõtelõigud</div>
          {(cap.segments || []).length === 0 && <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{cap.note}</div>}
          {(cap.segments || []).map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 90px 1fr auto', gap: 6, padding: '3px 0', fontSize: '0.68rem', borderTop: i ? '1px solid var(--border)' : 'none' }}>
              <span>{s.type}</span><span>{fmtT(Date.parse(s.from))}–{fmtT(Date.parse(s.to))}</span>
              <span>{s.soc_from} → {s.soc_to} % · {fmt(s.energy_kwh, 2)} kWh</span>
              <b>{fmt(s.capacity_kwh)} kWh{s.capacity_ah ? ` / ${fmt(s.capacity_ah, 0)} Ah` : ''} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>±{fmt(s.uncertainty_pct, 0)} %</span></b>
            </div>
          ))}
          {dev.settings_changed?.length > 0 && (
            <>
              <div style={{ ...label, marginTop: 10 }}>Mõõtmise ajal muudetud seaded</div>
              {dev.settings_changed.map((x, i) => <div key={i} style={{ fontSize: '0.68rem' }}>{x.name}: {String(x.from)} → {String(x.to)} {x.unit}</div>)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ServiceMonitorPage;
